import { useMemo } from 'react';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import PanoramaFishEyeIcon from '@mui/icons-material/PanoramaFishEye';

import { selectLogisticsPinnedFilters, togglePinnedFiltersState } from '@store';

import { useHasFilters } from 'hooks/common';

import { LOGISTICS_FIELDS_MAPPER } from 'components/logistics/constants';

const useLogisticsFilterConfig = ({ servicePoints, assignees }) => {
  const router = useRouter();

  const pinnedFilters = useSelector(selectLogisticsPinnedFilters);

  const dispatch = useDispatch();

  const filterCategories = useMemo(
    () => [
      {
        id: LOGISTICS_FIELDS_MAPPER.servicePoint.key,
        label: LOGISTICS_FIELDS_MAPPER.servicePoint.label,
        icon: PanoramaFishEyeIcon,
        options: LOGISTICS_FIELDS_MAPPER.servicePoint.options,
        multiSelect: true,
      },
      {
        id: LOGISTICS_FIELDS_MAPPER.category.key,
        label: LOGISTICS_FIELDS_MAPPER.category.label,
        icon: PanoramaFishEyeIcon,
        options: LOGISTICS_FIELDS_MAPPER.category.options,
        multiSelect: true,
      },
      {
        id: LOGISTICS_FIELDS_MAPPER.status.key,
        label: LOGISTICS_FIELDS_MAPPER.status.label,
        icon: PanoramaFishEyeIcon,
        options: LOGISTICS_FIELDS_MAPPER.status.options,
        multiSelect: true,
      },
      {
        id: LOGISTICS_FIELDS_MAPPER.pickUpLocation.key,
        label: LOGISTICS_FIELDS_MAPPER.pickUpLocation.label,
        icon: PanoramaFishEyeIcon,
        options: servicePoints,
        multiSelect: true,
      },
      {
        id: LOGISTICS_FIELDS_MAPPER.dropOffLocation.key,
        label: LOGISTICS_FIELDS_MAPPER.dropOffLocation.label,
        icon: PanoramaFishEyeIcon,
        options: servicePoints,
        multiSelect: true,
      },
      {
        id: LOGISTICS_FIELDS_MAPPER.assignee.key,
        label: LOGISTICS_FIELDS_MAPPER.assignee.label,
        icon: PanoramaFishEyeIcon,
        options: [{ text: 'None', value: 'None' }, ...assignees],
        multiSelect: true,
      },
      {
        id: LOGISTICS_FIELDS_MAPPER.owner.key,
        label: LOGISTICS_FIELDS_MAPPER.owner.label,
        icon: PanoramaFishEyeIcon,
        options: LOGISTICS_FIELDS_MAPPER.owner.options,
        multiSelect: true,
      },
    ],
    [servicePoints, assignees]
  );
  const { hasFilters } = useHasFilters({ pinnedFilters });

  const currentFilters = useMemo(() => {
    const parseQueryValue = (value) => {
      if (!value) return [];
      if (typeof value === 'string') {
        return value.split(',').filter(Boolean);
      }
      return Array.isArray(value) ? value : [];
    };

    return {
      [LOGISTICS_FIELDS_MAPPER.servicePoint.key]: parseQueryValue(
        router.query[LOGISTICS_FIELDS_MAPPER.servicePoint.key]
      ),
      [LOGISTICS_FIELDS_MAPPER.category.key]: parseQueryValue(
        router.query[LOGISTICS_FIELDS_MAPPER.category.key]
      ),
      [LOGISTICS_FIELDS_MAPPER.status.key]: parseQueryValue(
        router.query[LOGISTICS_FIELDS_MAPPER.status.key]
      ),
      [LOGISTICS_FIELDS_MAPPER.pickUpLocation.key]: parseQueryValue(
        router.query[LOGISTICS_FIELDS_MAPPER.pickUpLocation.key]
      ),
      [LOGISTICS_FIELDS_MAPPER.dropOffLocation.key]: parseQueryValue(
        router.query[LOGISTICS_FIELDS_MAPPER.dropOffLocation.key]
      ),
      [LOGISTICS_FIELDS_MAPPER.assignee.key]: parseQueryValue(
        router.query[LOGISTICS_FIELDS_MAPPER.assignee.key]
      ),
      [LOGISTICS_FIELDS_MAPPER.owner.key]: parseQueryValue(
        router.query[LOGISTICS_FIELDS_MAPPER.owner.key]
      ),
    };
  }, [router.query]);

  const handlePinnedFilterSelect = (pinnedFilter) => {
    dispatch(togglePinnedFiltersState({ board: 'logistics', pinnedFilter }));
  };

  return {
    filterCategories,
    currentFilters,
    pinnedFilters,
    supportPinnedFilters: true,
    hasFilters,
    handlePinnedFilterSelect,
  };
};

export default useLogisticsFilterConfig;
